import { supabase } from '../lib/supabase';

const LEVELS = ['overview', 'level_1', 'level_2', 'level_3', 'level_4', 'level_5'];
const LEVEL_LABELS = {
  overview: 'Overview',
  level_1: 'Level 1',
  level_2: 'Level 2',
  level_3: 'Level 3',
  level_4: 'Level 4',
  level_5: 'Level 5',
};

/**
 * Summary stat cards: total views, avg completion rate, total rewatches, unique viewers
 */
export const getPlaybackSummaryStats = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    const { data, error } = await supabase?.from('video_watch_events')?.select('user_id, completed, replay_count, watch_percentage')?.gte('watched_at', since?.toISOString());

    if (error) throw error;

    const events = data || [];
    const totalViews = events?.length;
    const completedEvents = events?.filter(e => e?.completed);
    const avgCompletionRate = totalViews > 0
      ? Math.round((completedEvents?.length / totalViews) * 100)
      : 0;
    const totalRewatches = events?.reduce((sum, e) => sum + (e?.replay_count || 0), 0);
    const uniqueViewers = new Set(events.map(e => e?.user_id).filter(Boolean))?.size;

    return {
      data: { totalViews, avgCompletionRate, totalRewatches, uniqueViewers }
    };
  } catch (err) {
    console.error('getPlaybackSummaryStats error:', err);
    return { error: err };
  }
};

/**
 * View counts and completion rate table grouped by song with level breakdown
 */
export const getSongLevelBreakdown = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    // Fetch watch events
    const { data: watchEvents, error: watchError } = await supabase?.from('video_watch_events')?.select('video_id, lesson_level, completed, replay_count, user_id, watch_percentage')?.gte('watched_at', since?.toISOString());

    if (watchError) throw watchError;

    // Fetch videos with song info
    const { data: videos, error: videosError } = await supabase?.from('videos')?.select('id, song_id, level, songs(id, title)')?.eq('published', true);

    if (videosError) throw videosError;

    const allVideos = videos || [];
    const allEvents = watchEvents || [];

    // Build video -> song map
    const videoSongMap = {};
    allVideos?.forEach(v => {
      videoSongMap[v?.id] = {
        songId: v?.song_id,
        songTitle: v?.songs?.title || 'Unknown Song',
        videoLevel: v?.level || 'overview',
      };
    });

    // Group events by song
    const songMap = {};
    allEvents?.forEach(e => {
      const videoInfo = videoSongMap?.[e?.video_id];
      if (!videoInfo?.songId) return;

      const { songId, songTitle } = videoInfo;
      const level = e?.lesson_level || videoInfo?.videoLevel || 'overview';

      if (!songMap?.[songId]) {
        songMap[songId] = { songId, songTitle, levels: {}, totalViews: 0, totalCompleted: 0 };
      }
      if (!songMap?.[songId]?.levels?.[level]) {
        songMap[songId].levels[level] = { views: 0, completed: 0, rewatches: 0 };
      }

      songMap[songId].levels[level].views += 1;
      songMap[songId].totalViews += 1;
      if (e?.completed) {
        songMap[songId].levels[level].completed += 1;
        songMap[songId].totalCompleted += 1;
      }
      songMap[songId].levels[level].rewatches += (e?.replay_count || 0);
    });

    // Convert to array with level breakdown
    const rows = Object.values(songMap)?.map(song => {
      const levelBreakdown = LEVELS?.map(lvl => {
        const cell = song?.levels?.[lvl] || { views: 0, completed: 0, rewatches: 0 };
        return {
          level: lvl,
          label: LEVEL_LABELS?.[lvl],
          views: cell?.views,
          completionRate: cell?.views > 0 ? Math.round((cell?.completed / cell?.views) * 100) : null,
          rewatches: cell?.rewatches,
        };
      });

      const overallCompletionRate = song?.totalViews > 0
        ? Math.round((song?.totalCompleted / song?.totalViews) * 100)
        : 0;

      return {
        songId: song?.songId,
        songTitle: song?.songTitle,
        totalViews: song?.totalViews,
        overallCompletionRate,
        levelBreakdown,
      };
    });

    rows?.sort((a, b) => b?.totalViews - a?.totalViews);
    return { data: rows };
  } catch (err) {
    console.error('getSongLevelBreakdown error:', err);
    return { error: err };
  }
};

/**
 * Talent engagement table: each talent's total views, avg completion rate, rewatch count
 */
export const getTalentEngagement = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    const { data: watchEvents, error: watchError } = await supabase?.from('video_watch_events')?.select('video_id, completed, replay_count, watch_percentage')?.gte('watched_at', since?.toISOString());

    if (watchError) throw watchError;

    // Fetch videos with talent info
    const { data: videos, error: videosError } = await supabase?.from('videos')?.select('id, talent_id, talent_profiles(id, display_name)')?.eq('published', true);

    if (videosError) throw videosError;

    const allVideos = videos || [];
    const allEvents = watchEvents || [];

    // Build video -> talent map
    const videoTalentMap = {};
    allVideos?.forEach(v => {
      if (v?.talent_id) {
        videoTalentMap[v?.id] = {
          talentId: v?.talent_id,
          talentName: v?.talent_profiles?.display_name || 'Unknown Talent',
        };
      }
    });

    // Group by talent
    const talentMap = {};
    allEvents?.forEach(e => {
      const talentInfo = videoTalentMap?.[e?.video_id];
      if (!talentInfo?.talentId) return;

      const { talentId, talentName } = talentInfo;
      if (!talentMap?.[talentId]) {
        talentMap[talentId] = { talentId, talentName, totalViews: 0, completed: 0, totalRewatches: 0 };
      }
      talentMap[talentId].totalViews += 1;
      if (e?.completed) talentMap[talentId].completed += 1;
      talentMap[talentId].totalRewatches += (e?.replay_count || 0);
    });

    const rows = Object.values(talentMap)?.map(t => ({
      talentId: t?.talentId,
      talentName: t?.talentName,
      totalViews: t?.totalViews,
      avgCompletionRate: t?.totalViews > 0 ? Math.round((t?.completed / t?.totalViews) * 100) : 0,
      totalRewatches: t?.totalRewatches,
    }));

    rows?.sort((a, b) => b?.totalViews - a?.totalViews);
    return { data: rows };
  } catch (err) {
    console.error('getTalentEngagement error:', err);
    return { error: err };
  }
};

/**
 * Rewatch patterns by level: which levels get rewatched most
 */
export const getRewatchPatternsByLevel = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    const { data, error } = await supabase?.from('video_watch_events')?.select('lesson_level, replay_count')?.gte('watched_at', since?.toISOString());

    if (error) throw error;

    const events = data || [];

    const byLevel = LEVELS?.map(level => {
      const levelEvents = events?.filter(e => e?.lesson_level === level);
      const totalRewatches = levelEvents?.reduce((sum, e) => sum + (e?.replay_count || 0), 0);
      const eventsWithRewatches = levelEvents?.filter(e => (e?.replay_count || 0) > 0)?.length;
      const rewatchRate = levelEvents?.length > 0
        ? Math.round((eventsWithRewatches / levelEvents?.length) * 100)
        : 0;
      const avgRewatches = levelEvents?.length > 0
        ? parseFloat((totalRewatches / levelEvents?.length)?.toFixed(1))
        : 0;

      return {
        level,
        label: LEVEL_LABELS?.[level],
        totalRewatches,
        rewatchRate,
        avgRewatches,
        totalViews: levelEvents?.length,
      };
    });

    return { data: byLevel };
  } catch (err) {
    console.error('getRewatchPatternsByLevel error:', err);
    return { error: err };
  }
};

/**
 * Top and bottom performers by completion rate (songs)
 */
export const getTopBottomPerformersBySong = async (dateRange = 30) => {
  try {
    const result = await getSongLevelBreakdown(dateRange);
    if (result?.error) throw result?.error;

    const songs = (result?.data || [])?.filter(s => s?.totalViews >= 3); // min 3 views for meaningful data
    const sorted = [...songs]?.sort((a, b) => b?.overallCompletionRate - a?.overallCompletionRate);

    const top = sorted?.slice(0, 5);
    const bottom = sorted?.slice(-5)?.reverse();

    return { data: { top, bottom } };
  } catch (err) {
    console.error('getTopBottomPerformersBySong error:', err);
    return { error: err };
  }
};

export { LEVELS, LEVEL_LABELS };
